import jwtDecode from 'jwt-decode'
import isBefore from 'date-fns/isBefore'
import fromUnixTime from 'date-fns/fromUnixTime'
import { useQueryStringParams } from '../../../utils/queryParams'
import { keepProperties } from '../../../utils/objects'
import { useCallback, useEffect, useState } from 'react'
import { ParsedQuery } from 'query-string'

type AuthParams = {
  token: string
}

export type TokenInfo = {
  token: string
  tokenType: string
  expiry: number
}

type TokenContents = {
  sub: string
  clientToken: string
  iat: number
  exp: number
}

const tokenStorageKey = 'govToken'
const tokenTypeStorageKey = 'govTokenType'
const tokenExpiryKey = 'govTokenExpiry'
const authTokenType = 'Bearer'

const authParamNames: Array<keyof AuthParams> = ['token']

export function decodeToken(token: string): TokenInfo {
  const decodedToken = jwtDecode<TokenContents>(token)

  return {
    token: token,
    tokenType: authTokenType,
    expiry: decodedToken.exp
  }
}

function clearToken(): void {
  sessionStorage.removeItem(tokenStorageKey)
  sessionStorage.removeItem(tokenTypeStorageKey)
  sessionStorage.removeItem(tokenExpiryKey)
}

function saveToken({ token, tokenType, expiry }: TokenInfo): void {
  clearToken()
  sessionStorage.setItem(tokenStorageKey, token)
  sessionStorage.setItem(tokenTypeStorageKey, tokenType)
  sessionStorage.setItem(
    tokenExpiryKey,
    expiry.toString(),
  )
}

function getTokenInfo(): TokenInfo | null {
  const token = sessionStorage.getItem(tokenStorageKey)
  const tokenType = sessionStorage.getItem(tokenTypeStorageKey)
  const expiry = Number(sessionStorage.getItem(tokenExpiryKey)) || null

  if (!token || !tokenType || !expiry) return null

  return {
    token,
    tokenType,
    expiry
  }
}

export const isTokenActive = (tokenInfo: TokenInfo): boolean => {
  if (!tokenInfo.expiry) {
    return false
  }

  const expiryDate = tokenInfo.expiry ? fromUnixTime(tokenInfo.expiry) : null
  return expiryDate ? isBefore(new Date(), expiryDate) : false
}

const parseAuthParams = (queryParams: ParsedQuery): AuthParams | null => {
  const authParams = keepProperties<AuthParams>(queryParams, authParamNames)
  return Object.keys(authParams).length > 0 ? authParams : null
}

export function useAuthParams(): AuthParams | null {
  const queryParams = useQueryStringParams<AuthParams>()
  const [params, setParams] = useState<AuthParams | null>(parseAuthParams(queryParams))

  useEffect(() => {
    setParams(parseAuthParams(queryParams))
  }, [queryParams])

  return params
}

export function useStoredAuthToken(): TokenInfo | null {
  const tokenInfo = getTokenInfo()
  return tokenInfo && isTokenActive(tokenInfo) ? tokenInfo : null
}

export function useSaveAuthToken(): (tokenInfo: TokenInfo) => void {
  const saveAuthToken = useCallback((tokenInfo: TokenInfo) => {
    if (tokenInfo.token !== getTokenInfo()?.token) {
      saveToken(tokenInfo)
    }
  }, [])

  return saveAuthToken
}

export function useClearToken(): () => void {
  return clearToken
}
